import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellOff, CheckCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/platform/AppShell";
import { EmptyState, PageTitle } from "@/components/platform/Badges";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/platform";

const SEV = { high: "border-l-red-500", medium: "border-l-amber-500", info: "border-l-[#008080]" };

export default function Notifications() {
  const auth = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/platform/notifications", { params: { unread_only: unreadOnly, limit: 200 } });
    setData(d);
  }, [unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    const { data: d } = await api.post("/platform/notifications/scan");
    const n = Object.values(d.created || {}).reduce((a, b) => a + b, 0);
    toast.success(`Scan complete · ${n} new notification${n === 1 ? "" : "s"}`);
    load();
  };

  const open = async (n) => {
    if (!n.read) await api.post(`/platform/notifications/${n.id}/read`);
    if (n.link) nav(n.link); else load();
  };

  return (
    <AppShell testId="notifications-page">
      <PageTitle eyebrow="Notification engine" title="Notifications"
                 subtitle="Overdue tasks, pending approvals, approaching SLAs and overdue reviews. Each alert is also queued for email delivery."
                 actions={<>
                   <Button variant="outline" onClick={() => setUnreadOnly((u) => !u)} data-testid="notifications-toggle-unread">
                     {unreadOnly ? "Show all" : "Unread only"}
                   </Button>
                   {auth.can("notifications", "edit") && (
                     <Button variant="outline" onClick={scan} data-testid="notifications-scan"><RefreshCw className="w-4 h-4 mr-1" /> Run scan</Button>
                   )}
                   <Button onClick={async () => { await api.post("/platform/notifications/read-all"); load(); }}
                           className="bg-[#003366] hover:bg-[#003366]/90" disabled={!data.unread} data-testid="notifications-read-all">
                     <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
                   </Button>
                 </>} />
      {data.items.length === 0 ? (
        <EmptyState icon={BellOff} title="No notifications" hint="You're all caught up." testId="notifications-empty" />
      ) : (
        <ul className="space-y-2 max-w-3xl" data-testid="notifications-list">
          {data.items.map((n) => (
            <li key={n.id}>
              <button onClick={() => open(n)} data-testid={`notifications-row-${n.id}`}
                      className={`w-full text-left bg-white border border-slate-200 border-l-4 ${SEV[n.severity] || SEV.info} rounded-lg px-4 py-3 hover:shadow-sm transition-shadow ${n.read ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{n.type_label}</span>
                  <span className="text-[10px] font-mono text-slate-400">{fmtDT(n.created_at)}</span>
                </div>
                <div className="text-sm font-semibold text-[#003366]">{n.title}</div>
                <div className="text-xs text-slate-600">{n.message}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
